import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ThemeProviderProps {
    children: ReactNode;
}

const ThemeContext = createContext({
    isDarkTheme: false,
    toggleTheme: () => {},
});

/**
 * Componente `ThemeProvider` que provee contexto para manejar el tema oscuro.
 * 
 * - Mantiene el estado `isDarkTheme` para indicar si el tema oscuro está activo.
 * - Función `toggleTheme` para alternar el tema oscuro y actualizar la clase CSS en `<html>`.
 * - Provee a los componentes hijos el estado y la función para cambiar tema mediante `ThemeContext`.
 * 
 * @param children Componentes hijos que tendrán acceso al contexto de tema.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [isDarkTheme, setIsDarkTheme] = useState(false);

    const toggleTheme = () => {
        setIsDarkTheme((prev) => !prev);
        document.documentElement.classList.toggle('dark-theme');
    };

    return (
        <ThemeContext.Provider value={{ isDarkTheme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

/**
 * Hook personalizado `useTheme` para consumir el contexto del tema.
 * 
 * Retorna el contexto `ThemeContext` con el estado `isDarkTheme` y la función `toggleTheme`.
 * Debe usarse dentro de un componente hijo de `ThemeProvider`.
 */
export const useTheme = () => useContext(ThemeContext);